/**
 * svgTriangles — outillage SVG partagé du dossier triangles (5e, thème #10).
 *
 * Réutilise le moteur de figure de svgAngles (creerFigure : segment, point,
 * marque d'angle en POLYLIGNE, codageDroit, codageLongueur) et ajoute ce qui
 * manque pour les triangles : construction d'un triangle EXACT à partir de deux
 * angles (loi des sinus) ou de trois côtés (SSS), marque de l'angle INTÉRIEUR à
 * un sommet, tracé d'un cercle (circonscrit), et un cadrage automatique qui
 * met la figure à l'échelle dans le viewBox (échelle uniforme → angles et
 * égalités de longueurs conservés).
 *
 * Markup balisé pour le solveur géométrique (tests/solvers/triangles.js) :
 *   <text class="mv">72°</text>  mesure d'angle marquée (posée sur la bissectrice)
 *   <g class="pt">…<text class="pl">A</text></g>  sommet nommé
 * Les figures « calcul d'angle » n'exposent QUE les mesures connues (jamais le
 * « ? »), donc le solveur lit exactement les données de l'énoncé.
 */

import {
    creerFigure, ptAt, inter, uv,
    BLEU, ROUGE, VERT, chapeau, nomAngle, imgElement,
} from '../angles/svgAngles';

export {
    creerFigure, ptAt, inter, uv, BLEU, ROUGE, VERT, chapeau, nomAngle, imgElement,
};

/** Élément additionnel d'énoncé : SVG encodé en data-URI (lu tel quel par le solveur). */
export const figEl = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageData: `data:image/svg+xml,${encodeURIComponent(svg)}` } }],
});

/** Angle mathématique (deg) de P vers Q (écran y inversé). */
export const angleVers = (P, Q) => (Math.atan2(-(Q.y - P.y), Q.x - P.x) * 180) / Math.PI;

/** Différence d'angles ramenée dans (−180, 180]. */
const deltaSigne = (d1, d2) => (((d2 - d1) % 360) + 540) % 360 - 180;

/**
 * Met une liste de points à l'échelle pour remplir le viewBox (marge pad),
 * échelle UNIFORME (angles conservés, longueurs égales restent égales).
 * Retourne une fonction map(point)→point transformé, appliquée à tous les
 * points nommés AVANT de tracer.
 */
export const cadreur = (pts, vb, pad = 42) => {
    const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const dx = maxX - minX || 1, dy = maxY - minY || 1;
    const k = Math.min((vb.w - 2 * pad) / dx, (vb.h - 2 * pad) / dy);
    const offX = (vb.w - k * dx) / 2, offY = (vb.h - k * dy) / 2;
    return (P) => ({ x: offX + (P.x - minX) * k, y: offY + (P.y - minY) * k });
};

/**
 * Triangle EXACT de sommets A (gauche), B (droite), C (haut) réalisant les
 * angles intérieurs angA en A et angB en B (donc 180−angA−angB en C).
 * Base [AB] horizontale, C au-dessus. Coordonnées « brutes » (à cadrer ensuite).
 */
export const triangleParAngles = (angA, angB, base = 200, A0 = { x: 0, y: 0 }) => {
    const A = { x: A0.x, y: A0.y };
    const B = { x: A0.x + base, y: A0.y };
    const C = inter(A, angA, B, 180 - angB); // rayon +angA depuis A, 180−angB depuis B
    return { A, B, C };
};

/**
 * Triangle EXACT de côtés a=BC, b=CA, c=AB (SSS). Base [AB]=c horizontale,
 * C au-dessus. Longueurs en unités brutes (cadrées ensuite). Renvoie null si
 * non constructible (inégalité triangulaire).
 */
export const triangleParCotes = (a, b, c, A0 = { x: 0, y: 0 }) => {
    if (a + b <= c || a + c <= b || b + c <= a) return null;
    const A = { x: A0.x, y: A0.y };
    const B = { x: A0.x + c, y: A0.y };
    const cosA = (b * b + c * c - a * a) / (2 * b * c);
    const angA = (Math.acos(Math.max(-1, Math.min(1, cosA))) * 180) / Math.PI;
    const C = ptAt(A, angA, b);
    return { A, B, C, angA };
};

/**
 * Marque l'angle INTÉRIEUR au sommet V du triangle (entre les côtés VP et VQ) :
 * le secteur est TOUJOURS le plus petit (interior < 180°). texte = « 72° » ou
 * « ? ». Renvoie fig (chaînable).
 */
export const marqueInterieur = (fig, V, P, Q, texte, opts = {}) => {
    const d1 = angleVers(V, P);
    const d2 = angleVers(V, Q);
    const delta = deltaSigne(d1, d2);
    const [a1, a2] = delta >= 0 ? [d1, d1 + delta] : [d1 + delta, d1];
    return fig.marque(V, a1, a2, texte, opts);
};

/** Codage d'angle droit intérieur au sommet V (entre VP et VQ). */
export const codageDroitInterieur = (fig, V, P, Q, opts = {}) => {
    const d1 = angleVers(V, P);
    const d2 = angleVers(V, Q);
    const delta = deltaSigne(d1, d2);
    const [a1, a2] = delta >= 0 ? [d1, d1 + delta] : [d1 + delta, d1];
    return fig.codageDroit(V, a1, a2, opts);
};

/** Cercle (circonscrit…) de centre O, rayon r. */
export const cercle = (fig, O, r, { couleur = BLEU, epaisseur = 1.6, dash = null } = {}) => {
    const d = dash ? ` stroke-dasharray="${dash}"` : '';
    fig.brut(`<circle class="cc" cx="${O.x.toFixed(1)}" cy="${O.y.toFixed(1)}" r="${r.toFixed(1)}" fill="none" stroke="${couleur}" stroke-width="${epaisseur}"${d}/>`);
    return fig;
};

/** Petite croix (centre du cercle circonscrit) au point O. */
export const croix = (fig, O, { couleur = BLEU, c = 5 } = {}) => {
    fig.brut(`<path class="cx" d="M ${(O.x - c).toFixed(1)},${O.y.toFixed(1)} L ${(O.x + c).toFixed(1)},${O.y.toFixed(1)} M ${O.x.toFixed(1)},${(O.y - c).toFixed(1)} L ${O.x.toFixed(1)},${(O.y + c).toFixed(1)}" stroke="${couleur}" stroke-width="1.6"/>`);
    return fig;
};

/** Centre du cercle circonscrit d'un triangle ABC (intersection des médiatrices). */
export const circoncentre = (A, B, C) => {
    const d = 2 * (A.x * (B.y - C.y) + B.x * (C.y - A.y) + C.x * (A.y - B.y));
    if (Math.abs(d) < 1e-9) return null;
    const a2 = A.x * A.x + A.y * A.y;
    const b2 = B.x * B.x + B.y * B.y;
    const c2 = C.x * C.x + C.y * C.y;
    const ux = (a2 * (B.y - C.y) + b2 * (C.y - A.y) + c2 * (A.y - B.y)) / d;
    const uy = (a2 * (C.x - B.x) + b2 * (A.x - C.x) + c2 * (B.x - A.x)) / d;
    return { x: ux, y: uy };
};

/** Distance euclidienne. */
export const dist = (P, Q) => Math.hypot(Q.x - P.x, Q.y - P.y);

/** Milieu de [PQ]. */
export const milieu = (P, Q) => ({ x: (P.x + Q.x) / 2, y: (P.y + Q.y) / 2 });

/** Pied de la perpendiculaire abaissée de A sur la droite (BC). t = position sur [BC]. */
export const pied = (A, B, C) => {
    const vx = C.x - B.x, vy = C.y - B.y;
    const t = ((A.x - B.x) * vx + (A.y - B.y) * vy) / (vx * vx + vy * vy);
    return { x: B.x + t * vx, y: B.y + t * vy, t };
};

/** Segment pointillé entre P et Q (hauteur…). */
export const segmentPointille = (fig, P, Q, { couleur = '#b45309', epaisseur = 1.6 } = {}) => {
    fig.brut(`<line x1="${P.x.toFixed(1)}" y1="${P.y.toFixed(1)}" x2="${Q.x.toFixed(1)}" y2="${Q.y.toFixed(1)}" stroke="${couleur}" stroke-width="${epaisseur}" stroke-dasharray="5 4"/>`);
    return fig;
};

/**
 * Trace la hauteur issue de `apex` sur la base (B,C) : segment pointillé
 * apex→pied H + codage d'angle droit en H. Si le pied est HORS du segment
 * [BC] (triangle obtus), prolonge la base en pointillé jusqu'à H. Retourne H.
 */
export const traceHauteur = (fig, apex, B, C, { couleur = '#b45309' } = {}) => {
    const H = pied(apex, B, C);
    if (H.t < 0) segmentPointille(fig, B, H, { couleur: '#94a3b8' });
    else if (H.t > 1) segmentPointille(fig, C, H, { couleur: '#94a3b8' });
    segmentPointille(fig, apex, H, { couleur });
    codageDroitInterieur(fig, H, apex, H.t < 0.5 ? C : B, { couleur });
    return H;
};
